import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Logo } from "@shared/components/Logo";
import { UserMenu } from "@shared/components/UserMenu";
import { FullscreenToggle } from "../FullscreenToggle";
import { GlobalSearch } from "../GlobalSearch";
import { useGlobalSearch } from "../../lib/useGlobalSearch";
import { CenterSwitcher } from "@shared/components/CenterSwitcher";
import { NavLayoutSelector } from "./NavLayoutSelector";

/**
 * Top bar for the sidebar navigation layout. Unlike {@link AdminHeader} (used
 * by the islands/dashboard-cards layout) this header spans the full viewport
 * width so the search sits centered on the real screen center, the logo stays
 * pinned above the sidebar, and the surrounding controls get extra padding.
 */
export function AdminHeaderSidebar() {
  const navigate = useNavigate();
  const { isOpen, open, close, query, setQuery, results, gatedCount, loading } = useGlobalSearch();

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        open();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <header className="sticky top-0 z-40 bg-transparent px-6 py-4 md:px-8">
      <div className="flex items-center justify-between md:grid md:grid-cols-3 md:gap-6">
        {/* Left: Logo + Center Switcher (always visible above the sidebar) */}
        <div className="flex items-center gap-3 md:justify-self-start">
          <Link to="/">
            <Logo size="sm" showDots={false} />
          </Link>
          <CenterSwitcher />
        </div>

        {/* Center: Search (hidden on mobile) */}
        <div className="hidden md:block md:justify-self-center" data-tour="search">
          <button
            onClick={open}
            className="flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-200 bg-surface2/50 text-muted border border-border/30 hover:bg-surface2 hover:text-foreground"
          >
            <Search size={14} />
            Search
            <kbd className="ml-1 rounded bg-surface2 px-1.5 py-0.5 text-[10px] font-mono text-muted">
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Right: Controls (extra spacing for the wider bar) */}
        <div className="flex items-center gap-3 md:gap-5 md:justify-self-end">
          <div className="hidden md:flex md:items-center md:gap-5">
            <FullscreenToggle />
            <Link
              to="/history"
              className="rounded-full px-5 py-2 text-sm font-medium transition-all duration-200 bg-surface2/50 text-muted border border-border/30 hover:bg-surface2 hover:text-foreground"
            >
              History
            </Link>
            <button
              onClick={() => {
                sessionStorage.removeItem("whats-new-dismissed");
                navigate("/whats-new/state-licensing");
              }}
              className="rounded-full px-3 py-2 text-sm font-medium transition-all duration-200 bg-surface2/50 text-muted border border-border/30 hover:bg-surface2 hover:text-foreground flex items-center gap-1"
              title="What's New"
            >
              <span className="sr-only">What's New</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sparkles"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" /><path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" /></svg>
            </button>
            <div data-tour="user-menu">
              <UserMenu extraSection={<NavLayoutSelector />} />
            </div>
          </div>
        </div>
      </div>

      <GlobalSearch
        isOpen={isOpen}
        query={query}
        setQuery={setQuery}
        results={results}
        gatedCount={gatedCount}
        loading={loading}
        close={close}
      />
    </header>
  );
}
